import * as SelectPrimitive from '@radix-ui/react-select';

import { Check, ChevronDown, ChevronUp } from 'lucide-react';

import {
  Children,
  forwardRef,
  isValidElement,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
  type SelectHTMLAttributes,
} from 'react';

import { cn } from '@/lib/utils';

import { controlSurface } from './control-styles';

export const SELECT_EMPTY_VALUE = '__empty__';
const EMPTY = SELECT_EMPTY_VALUE;

interface Choice {
  value: string;
  label: ReactNode;
  disabled?: boolean;
}

interface ChoiceGroup {
  label?: string;
  choices: Choice[];
}

function encode(value: string): string {
  return value === '' ? EMPTY : value;
}

function decode(value: string): string {
  return value === EMPTY ? '' : value;
}

function readChoices(children: ReactNode): ChoiceGroup[] {
  const groups: ChoiceGroup[] = [];
  let loose: Choice[] = [];
  const flush = () => {
    if (loose.length) groups.push({ choices: loose });
    loose = [];
  };
  Children.forEach(children, (child) => {
    if (!isValidElement(child)) return;
    const element = child as ReactElement<{
      value?: string | number;
      children?: ReactNode;
      disabled?: boolean;
      label?: string;
    }>;
    if (element.type === 'optgroup') {
      flush();
      groups.push({
        label: element.props.label,
        choices: readChoices(element.props.children).flatMap((group) => group.choices),
      });
      return;
    }
    if (element.type === 'option') {
      const raw = element.props.value;
      const value =
        raw === undefined || raw === null ? String(element.props.children ?? '') : String(raw);
      loose.push({ value, label: element.props.children, disabled: element.props.disabled });
      return;
    }
    const nested = readChoices(element.props.children);
    if (nested.length) {
      flush();
      groups.push(...nested);
    }
  });
  flush();
  return groups;
}

export interface SelectChangeEvent {
  target: HTMLSelectElement;
  currentTarget: HTMLSelectElement;
  type: 'change';
}

export interface SelectProps extends Omit<
  SelectHTMLAttributes<HTMLSelectElement>,
  'onChange' | 'value' | 'defaultValue' | 'size'
> {
  value?: string | number | readonly string[];
  defaultValue?: string | number | readonly string[];
  onChange?: (event: SelectChangeEvent) => void;
  placeholder?: string;
  align?: 'start' | 'center' | 'end';
  contentClassName?: string;
  readOnly?: boolean;
}

export const selectContentClass = cn(
  'z-[70] max-h-[var(--radix-select-content-available-height)] min-w-[var(--radix-select-trigger-width)] overflow-hidden rounded-xl border border-border/70 bg-popover text-popover-foreground shadow-xl shadow-black/10 no-print dark:shadow-black/40',
  'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
  'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
  'data-[side=bottom]:slide-in-from-top-1 data-[side=top]:slide-in-from-bottom-1',
);

export const selectItemClass = cn(
  'relative flex w-full cursor-default select-none items-center gap-2 rounded-md py-2 pe-8 ps-2.5 text-sm outline-none transition-colors',
  'data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground',
  'data-[state=checked]:font-medium data-[state=checked]:text-primary',
  'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
);

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  {
    className,
    contentClassName,
    children,
    value,
    defaultValue,
    onChange,
    onBlur,
    placeholder,
    align = 'start',
    disabled,
    readOnly,
    required,
    name,
    id,
    ...rest
  },
  ref,
) {
  const hidden = useRef<HTMLSelectElement | null>(null);
  const controlled = value !== undefined;
  const [internal, setInternal] = useState(() =>
    Array.isArray(defaultValue) ? String(defaultValue[0] ?? '') : String(defaultValue ?? ''),
  );
  const current = controlled ? String(value ?? '') : internal;
  const wanted = useRef(current);
  wanted.current = current;
  const attach = useCallback((node: HTMLSelectElement | null) => {
    hidden.current = node;
    if (!node || Object.prototype.hasOwnProperty.call(node, 'value')) return;
    const native = Object.getOwnPropertyDescriptor(HTMLSelectElement.prototype, 'value');
    if (!native?.set) return;
    Object.defineProperty(node, 'value', {
      configurable: true,
      get: () => wanted.current,
      set: (next: unknown) => {
        wanted.current = String(next ?? '');
        native.set?.call(node, wanted.current);
        setInternal(wanted.current);
      },
    });
  }, []);
  useImperativeHandle(ref, () => hidden.current as HTMLSelectElement);
  const groups = readChoices(children);
  const all = groups.flatMap((group) => group.choices);
  const chosen = all.find((choice) => choice.value === current);
  const [open, setOpen] = useState(false);
  const optionsKey = all.map((choice) => choice.value).join('|');
  useEffect(() => {
    const node = hidden.current;
    if (!node) return;
    const native = Object.getOwnPropertyDescriptor(HTMLSelectElement.prototype, 'value');
    native?.set?.call(node, wanted.current);
  }, [optionsKey, current]);
  const emit = (next: string) => {
    const node = hidden.current;
    if (node) node.value = next;
    else if (!controlled) setInternal(next);
    if (onChange && node) onChange({ target: node, currentTarget: node, type: 'change' });
  };
  const ariaLabel = rest['aria-label'];
  const ariaInvalid = rest['aria-invalid'];
  const ariaDescribedBy = rest['aria-describedby'];
  return (
    <>
      <select
        ref={attach}
        name={name}
        value={current}
        onChange={(event) => emit(event.target.value)}
        onBlur={onBlur}
        required={required}
        disabled={disabled}
        tabIndex={-1}

        aria-hidden

        className="pointer-events-none fixed start-0 top-0 h-px w-px opacity-0"
      >
        {all.map((choice) => (
          <option key={choice.value} value={choice.value} disabled={choice.disabled}>
            {typeof choice.label === 'string' ? choice.label : choice.value}
          </option>
        ))}
      </select>

      <SelectPrimitive.Root
        value={encode(current)}
        onValueChange={(next) => {
          const decoded = decode(next);
          if (decoded === '' && !all.some((choice) => choice.value === '')) return;
          if (decoded !== current) emit(decoded);
        }}
        disabled={disabled || readOnly}
        open={open}
        onOpenChange={setOpen}
      >
        <SelectPrimitive.Trigger
          id={id}
          aria-label={ariaLabel}
          aria-invalid={ariaInvalid}
          aria-describedby={ariaDescribedBy}
          onBlur={(event) => onBlur?.(event as unknown as React.FocusEvent<HTMLSelectElement>)}
          className={cn(
            controlSurface,
            'flex h-9 cursor-pointer items-center justify-between gap-2 py-1 pe-2.5 ps-3 text-start',
            'data-[state=open]:border-ring data-[state=open]:ring-[3px] data-[state=open]:ring-ring/25',
            readOnly && 'cursor-default bg-muted/60 text-muted-foreground',
            '[&>span:first-child]:min-w-0 [&>span:first-child]:flex-1 [&>span:first-child]:truncate',
            className,
          )}
        >
          <SelectPrimitive.Value placeholder={placeholder ?? (chosen ? undefined : '')}>
            {chosen ? chosen.label : (placeholder ?? '')}
          </SelectPrimitive.Value>

          <SelectPrimitive.Icon asChild>
            <ChevronDown
              className={cn(
                'h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200',
                open && 'rotate-180',
              )}
              aria-hidden
            />
          </SelectPrimitive.Icon>
        </SelectPrimitive.Trigger>

        <SelectPrimitive.Portal>
          <SelectPrimitive.Content
            position="popper"
            sideOffset={6}
            align={align}
            collisionPadding={8}
            className={cn(selectContentClass, contentClassName)}
          >
            <SelectPrimitive.ScrollUpButton className="flex h-6 cursor-default items-center justify-center bg-popover text-muted-foreground">
              <ChevronUp className="h-4 w-4" aria-hidden />
            </SelectPrimitive.ScrollUpButton>

            <SelectPrimitive.Viewport className="p-1.5">
              {groups.map((group, index) => (
                <SelectPrimitive.Group key={group.label ?? index}>
                  {group.label && (
                    <SelectPrimitive.Label className="px-2.5 pb-1 pt-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                      {group.label}
                    </SelectPrimitive.Label>
                  )}
                  {group.choices.map((choice) => (
                    <SelectPrimitive.Item
                      key={choice.value}
                      value={encode(choice.value)}
                      disabled={choice.disabled}
                      className={selectItemClass}
                    >
                      <SelectPrimitive.ItemText>{choice.label}</SelectPrimitive.ItemText>

                      <SelectPrimitive.ItemIndicator className="absolute end-2.5 grid place-items-center">
                        <Check className="h-4 w-4 text-primary" aria-hidden />
                      </SelectPrimitive.ItemIndicator>
                    </SelectPrimitive.Item>
                  ))}
                </SelectPrimitive.Group>
              ))}
            </SelectPrimitive.Viewport>

            <SelectPrimitive.ScrollDownButton className="flex h-6 cursor-default items-center justify-center bg-popover text-muted-foreground">
              <ChevronDown className="h-4 w-4" aria-hidden />
            </SelectPrimitive.ScrollDownButton>
          </SelectPrimitive.Content>
        </SelectPrimitive.Portal>
      </SelectPrimitive.Root>
    </>
  );
});
